import type { ImageDto } from "@/api/types";
import { ClockIcon } from "@/components/Icons/ClockIcon";
import { MarkerPin03Icon } from "@/components/Icons/MarkerPin03Icon";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { useTranslation } from "@/translations";

import { EntityPreviewCardWithBackground } from "./EntityPreviewCardWithBackground";
import { formatEventDate } from "./eventDateFormat";
import { EventSchedule } from "./EventSchedule";

export function EventPreviewCard({
  title,
  startsAt,
  endsAt,
  location,
  image,
  fallbackImage,
  imageUrl,
  href,
  onClick,
}: {
  title: string;
  startsAt: Date | string;
  endsAt?: Date | string;
  location?: string;
  image?: ImageDto;
  /** Used when neither imageUrl nor image is available. */
  fallbackImage?: ImageDto;
  imageUrl?: string;
  href?: string;
  onClick?: () => void;
}): React.ReactNode {
  const { i18n } = useTranslation();
  const date = new Date(startsAt);

  return (
    <EntityPreviewCardWithBackground
      presentation="event"
      title={title}
      href={href}
      onClick={onClick}
      headerBackgroundImage={image}
      fallbackBackgroundImage={fallbackImage}
      headerBackgroundImageUrl={imageUrl}
      renderHeader={() => (
        <section className="flex h-full items-start p-space-md">
          <time
            dateTime={date.toISOString()}
            className="flex flex-col items-center rounded-md bg-background-default p-space-sm"
          >
            <Text variant="body-emphasis" tone="secondary" align="center">
              {formatEventDate(i18n.language, date)}
            </Text>
          </time>
        </section>
      )}
      renderSubtitle={() => (
        <Stack gap="xs">
          {location && (
            <Inline gap="xs" align="center" wrap={false}>
              <MarkerPin03Icon size={16} tone="secondary" />
              <p className="min-w-0 truncate text-caption text-text-primary">{location}</p>
            </Inline>
          )}
          {endsAt && (
            <Inline gap="xs" align="center" wrap={false}>
              <ClockIcon size={16} tone="secondary" />
              <p className="text-caption text-text-primary">
                <EventSchedule startsAt={startsAt} endsAt={endsAt} />
              </p>
            </Inline>
          )}
        </Stack>
      )}
    />
  );
}
